/**
 * Tag Domain Zod Validation Schemas
 *
 * Task: T-P5-D8-F1-2
 *
 * Validation schemas for tag colors and names.
 */

import { z } from 'zod';
import {
  TAG_COLORS,
  TAG_INTENTS,
  TAG_DELETE_ACTIONS,
  OFFERING_TARGET_SHAPES,
  OFFERING_LINK_TYPES,
  TAG_RULE_MODES,
} from './types.js';

/** Validates admin-selectable tag color tokens. Rejects system-reserved colors. */
export const tagColorSchema = z.enum(TAG_COLORS);

/** Validates tag display names: 1-100 chars, trimmed, no whitespace-only. */
export const tagNameSchema = z.string().trim().min(1).max(100);

// ============================================================================
// F2: Request / Query Schemas
// ============================================================================

/** Single offering link tuple — target + shape + link type */
export const offeringLinkInputSchema = z.object({
  targetId: z.string().uuid(),
  targetShape: z.enum(OFFERING_TARGET_SHAPES),
  linkType: z.enum(OFFERING_LINK_TYPES),
});

/** POST /api/admin/tags — create a new tag */
export const createTagRequestSchema = z.object({
  name: tagNameSchema,
  description: z.string().trim().max(500).optional(),
  color: tagColorSchema,
  intent: z.enum(TAG_INTENTS),
  offeringLinks: z.array(offeringLinkInputSchema).optional(),
});

/** PATCH /api/admin/tags/:id — partial update */
export const updateTagRequestSchema = z.object({
  name: tagNameSchema.optional(),
  description: z.string().trim().max(500).nullish(),
  color: tagColorSchema.optional(),
  addLinks: z.array(offeringLinkInputSchema).optional(),
  removeLinks: z.array(offeringLinkInputSchema).optional(),
  confirmAccessGrant: z.boolean().optional(),
});

/** DELETE /api/admin/tags/:id — body specifies action */
export const deleteTagRequestSchema = z.object({
  action: z.enum(TAG_DELETE_ACTIONS),
  draftOfferingIds: z.array(z.string().uuid()).optional(),
});

/** GET /api/admin/tags — list query */
export const tagListQuerySchema = z.object({
  search: z.string().optional(),
  filter: z.string().optional(),
  orderBy: z.enum(['name', 'clients', 'newest']).default('newest'),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});

/** GET /api/admin/tags/offerings — offering picker query */
export const offeringPickerQuerySchema = z.object({
  search: z.string().optional(),
  shape: z.enum(OFFERING_TARGET_SHAPES).optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});

// ============================================================================
// F3: Client Tag Assignment Request Schemas
// ============================================================================

import { bulkClientSelectionSchema } from '../clients/schemas.js';

/** POST /api/admin/clients/:id/tags — add tags to a client */
export const addTagsToClientRequestSchema = z.object({
  tagIds: z.array(z.string().uuid()).min(1).max(50),
});

/** POST /api/admin/tags/:id/assign — tag-first bulk assign (tagId from URL) */
export const bulkTagAssignRequestSchema = z.object({
  selection: bulkClientSelectionSchema,
});

/** POST /api/admin/clients/bulk/tags/add|remove — client-first (tagId in body) */
export const bulkClientTagRequestSchema = z.object({
  selection: bulkClientSelectionSchema,
  tagId: z.string().uuid(),
});

/** POST /api/admin/clients/bulk/tags/common — common tags query */
export const commonTagsRequestSchema = z.object({
  selection: bulkClientSelectionSchema,
});

// ============================================================================
// F4-T5: Offering-Tag Admin Endpoints
// ============================================================================

/** GET /api/admin/offerings/:id/tags — query string for target shape */
export const offeringTagQuerySchema = z.object({
  targetShape: z.enum(OFFERING_TARGET_SHAPES),
});

/** POST /api/admin/offerings/:id/required-tags — add required tag */
export const addOfferingTagRequestSchema = z.object({
  tagId: z.string().uuid(),
  targetShape: z.enum(OFFERING_TARGET_SHAPES),
});

/** DELETE /api/admin/offerings/:id/required-tags/:tagId — remove required tag (querystring) */
export const removeOfferingTagQuerySchema = z.object({
  targetShape: z.enum(OFFERING_TARGET_SHAPES),
});

/** PATCH /api/admin/offerings/:id/required-tags/rule-mode — update rule mode */
export const updateRuleModeRequestSchema = z.object({
  mode: z.enum(TAG_RULE_MODES),
  targetShape: z.enum(OFFERING_TARGET_SHAPES),
});

/** POST /api/admin/offerings/:id/award-tags — add award tag */
export const addAwardTagRequestSchema = z.object({
  tagId: z.string().uuid(),
  targetShape: z.enum(OFFERING_TARGET_SHAPES),
});

/** DELETE /api/admin/offerings/:id/award-tags/:tagId — remove award tag (querystring) */
export const removeAwardTagQuerySchema = z.object({
  targetShape: z.enum(OFFERING_TARGET_SHAPES),
});

// ============================================================================
// F5: Attendance-Driven Tag Award DTOs
// ============================================================================

/** Per-attendee tag award result — returned by IClientTagAwardWriter */
export const tagAwardResultSchema = z.object({
  tagId: z.string(),
  tagName: z.string(),
  tagColor: tagColorSchema,
  isNew: z.boolean(),
});

/** Aggregate award summary across all attendees in a batch */
export const attendanceTagAwardSummarySchema = z.object({
  newAwards: z.array(z.object({
    tagId: z.string(),
    tagName: z.string(),
    tagColor: tagColorSchema,
    clientCount: z.number().int(),
    // P5 D8 F10: Unlock summary — how many offerings this tag unlocks
    unlockSummary: z.object({
      count: z.number().int(),
      firstOfferingName: z.string(),
    }).nullable().optional(),
  })),
  totalNewAwards: z.number().int(),
});
